import { SectionTitle } from "@/sprout/typography";
import useTasks from "@/hooks/use_tasks";
import SetupQuickApplyProfile from "./tasks/SetupQuickApplyProfile";
import SetupWorkPreference from "./tasks/SetupWorkPreference";
import AvailabilityTask from "./tasks/availability_task";
import SelfScheduleRequest from "./tasks/SelfScheduleRequest";
import CandidateSurvey from "./tasks/CandidateSurvey";
import ApplicationFormTask from "./tasks/application_form_task";
import ChooseDreamJob from "./tasks/ChooseDreamJob";
import SetupAccount from "./tasks/SetupAccount";
import IdentityVerificationTask from "./tasks/IdentityVerificationTask";
import TakeHomeTest from "./tasks/TakeHomeTest";
import ClearConfirmationModal from "@components/ClearConfirmationModal";
import type { GhrTask, Task } from "@/types/task";
import "./tasks_section.scss";
import { IconButton } from "@/sprout/icon_button";
import BackIcon from "@/sprout/icons/back";
import ForwardIcon from "@/sprout/icons/forward";
import { useRef, useState, useEffect, ComponentType } from "react";
import { useDashboardPreview } from "@components/dashboard/preview_context.tsx";

const TASK_COMPONENTS: Record<string, ComponentType<{ task: Task }>> = {
  SetupQuickApplyProfile,
  SetupWorkPreference,
  SetupAccount,
};

const GHR_TASK_COMPONENTS: Record<string, ComponentType<{ task: GhrTask }>> = {
  AvailabilityTask,
  SelfScheduleRequest,
  CandidateSurvey,
  TakeHomeTest,
  ApplicationFormTask,
  IdentityVerificationTask,
};

const ITEM_WIDTH = 300;

export const renderHeader = (count: number = 0) => (
  <div className="mb-4">
    <SectionTitle>{count ? `Tasks (${count})` : "Tasks"}</SectionTitle>
  </div>
);

const TasksSection = () => {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [spacerWidth, setSpacerWidth] = useState(0);
  const [isAtStart, setIsAtStart] = useState(true);
  const [isAtEnd, setIsAtEnd] = useState(false);

  const { preview } = useDashboardPreview();
  const { data: tasks } = useTasks({ mock: preview });

  useEffect(() => {
    const updateSpacerWidth = () => {
      if (containerRef.current) {
        const containerWidth = containerRef.current.offsetWidth;
        setSpacerWidth(containerWidth - ITEM_WIDTH);
      }
    };

    const updateScrollState = () => {
      if (scrollerRef.current) {
        const { scrollLeft, scrollWidth, clientWidth } = scrollerRef.current;
        const maxScrollLeft = scrollWidth - clientWidth;
        const displayWidth = clientWidth / ITEM_WIDTH;

        setIsAtStart(scrollLeft === 0);
        setIsAtEnd(scrollLeft >= maxScrollLeft - ITEM_WIDTH * displayWidth);
      }
    };

    const handleResize = () => {
      requestAnimationFrame(() => {
        updateSpacerWidth();
        updateScrollState();
      });
    };

    handleResize();

    const resizeObserver = new ResizeObserver(handleResize);

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    const handleWheelScroll = (event: WheelEvent) => {
      if (scrollerRef.current && isAtEnd && event.deltaX > 0) {
        event.preventDefault();
      }
    };

    if (scrollerRef.current) {
      scrollerRef.current.addEventListener("scroll", updateScrollState);
      scrollerRef.current.addEventListener("wheel", handleWheelScroll, { passive: false });
    }

    return () => {
      resizeObserver.disconnect();
      if (scrollerRef.current) {
        scrollerRef.current.removeEventListener("scroll", updateScrollState);
        scrollerRef.current.removeEventListener("wheel", handleWheelScroll);
      }
    };
  }, [tasks]);

  const next = () => {
    if (!scrollerRef.current) {
      return;
    }

    const maxScrollLeft = scrollerRef.current.scrollWidth - scrollerRef.current.clientWidth;

    scrollerRef.current.scrollBy({
      left: Math.min(ITEM_WIDTH, maxScrollLeft - scrollerRef.current.scrollLeft),
      top: 0,
      behavior: "smooth",
    });
  };

  const prev = () => {
    if (!scrollerRef.current) {
      return;
    }

    scrollerRef.current.scrollBy({
      left: -Math.min(ITEM_WIDTH, scrollerRef.current.scrollLeft),
      top: 0,
      behavior: "smooth",
    });
  };

  if (tasks.length === 0) {
    return null;
  }

  return (
    <div className="tasks-section p-6" ref={containerRef}>
      {renderHeader(tasks.filter((task) => !task.dismissed).length)}

      <div className="tasks-section__list flex gap-4" ref={scrollerRef}>
        {tasks.map((task: Task) => {
          if (task.taskable_type === "ChooseDreamJob") {
            return (
              <div className="tasks-section__item" key={task.id}>
                <ChooseDreamJob />
              </div>
            );
          }

          const TaskComponent =
            TASK_COMPONENTS[task.taskable_type] || GHR_TASK_COMPONENTS[task.taskable_type];

          if (TaskComponent) {
            return (
              <div className="tasks-section__item" key={task.id}>
                <TaskComponent task={task} />
              </div>
            );
          }
        })}
        {/* Spacer element for extra scrollable space */}
        <div style={{ minWidth: spacerWidth }} />
      </div>
      {tasks.length > 1 && (
        <div className="tasks-section__scroll-buttons flex justify-end">
          <IconButton
            label="Scroll left"
            icon={BackIcon}
            size="md"
            onClick={prev}
            disabled={isAtStart}
          />
          <IconButton
            label="Scroll right"
            icon={ForwardIcon}
            size="md"
            onClick={next}
            disabled={isAtEnd}
          />
        </div>
      )}

      <ClearConfirmationModal />
    </div>
  );
};

export default TasksSection;
